import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { cinemas, cities, cinemaBrands } from "@/mocks/cinemas";
import { getMoviesForCinema, getSchedulesByCinema, scheduleDates } from "@/lib/data";

const PAGE_SIZE = 6;

export default function CinemasPage() {
  const [searchParams] = useSearchParams();
  const initialCity = searchParams.get("city") || "全部";
  const initialBrand = searchParams.get("brand") || "全部";

  const [city, setCity] = useState(cities.includes(initialCity) ? initialCity : "全部");
  const [brand, setBrand] = useState(
    cinemaBrands.includes(initialBrand) ? initialBrand : "全部",
  );
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string[]>([]);

  const today = scheduleDates[0];

  const filtered = useMemo(
    () =>
      cinemas.filter((c) => {
        if (city !== "全部" && c.city !== city) return false;
        if (brand !== "全部" && c.brand !== brand) return false;
        return true;
      }),
    [city, brand],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
    }`;

  const toggle = (id: string) =>
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影院排片" }]} />

        <div className="mt-5 mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            影院列表
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            选择城市与品牌，查看合作影院的地址、联系方式与今日排片简览。
          </p>
        </div>

        <div className="space-y-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-14 shrink-0 text-sm text-foreground-500">城市</span>
            {cities.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCity(c);
                  setPage(1);
                }}
                className={chipClass(city === c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-14 shrink-0 text-sm text-foreground-500">品牌</span>
            {["全部", ...cinemaBrands].map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => {
                  setBrand(b);
                  setPage(1);
                }}
                className={chipClass(brand === b)}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-foreground-500">
          <span>共 {filtered.length} 家影院</span>
          <span>第 {safePage} / {totalPages} 页</span>
        </div>

        {pageItems.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-building-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              暂无符合条件的影院
            </h3>
            <p className="mt-2 text-sm text-foreground-500">试试切换其它城市或品牌。</p>
            <button
              type="button"
              onClick={() => {
                setCity("全部");
                setBrand("全部");
                setPage(1);
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              查看全部影院
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {pageItems.map((cinema) => {
              const isOpen = expanded.includes(cinema.id);
              const cinemaSchedules = getSchedulesByCinema(cinema.id, today);
              const movieIds = Array.from(new Set(cinemaSchedules.map((s) => s.movieId)));
              const moviesToday = getMoviesForCinema(cinema.id, today);
              return (
                <div
                  key={cinema.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                >
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="h-32 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-48">
                      <img
                        src={cinema.images[0]}
                        alt={`${cinema.name} 影院环境`}
                        title={`${cinema.name} 九七电影院`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                            <i className="ri-film-line"></i>
                          </span>
                          <div>
                            <Link
                              to={`/cinema/${cinema.id}`}
                              className="font-heading text-base font-semibold text-foreground-950 transition-colors hover:text-primary-600"
                            >
                              {cinema.name}
                            </Link>
                            <span className="ml-2 rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                              {cinema.brand}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-accent-600">
                          {cinema.rating.toFixed(1)} 分
                        </span>
                      </div>
                      <p className="mt-2 flex items-center gap-1 text-sm text-foreground-500">
                        <i className="ri-map-pin-line"></i>
                        {cinema.address}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-sm text-foreground-500">
                        <i className="ri-phone-line"></i>
                        {cinema.phone}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Link
                          to={`/cinema/${cinema.id}`}
                          className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                        >
                          查看排片
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggle(cinema.id)}
                          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                        >
                          {isOpen ? "收起排片" : "展开今日排片"}
                          <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 border-t border-background-200 pt-4">
                      {movieIds.length === 0 ? (
                        <p className="text-sm text-foreground-500">今日暂无排片信息。</p>
                      ) : (
                        <div className="space-y-3">
                          {moviesToday.map((movie) => (
                            <div key={movie.id} className="flex flex-wrap items-center gap-3">
                              <span className="w-28 shrink-0 truncate text-sm font-medium text-foreground-900">
                                {movie.title}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {cinemaSchedules
                                  .filter((s) => s.movieId === movie.id)
                                  .map((s) => (
                                    <Link
                                      key={s.id}
                                      to={`/schedule/${s.id}`}
                                      className="rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:bg-accent-50"
                                    >
                                      {s.time} · {s.hall}
                                    </Link>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Pagination current={safePage} total={totalPages} onChange={setPage} />
      </div>
    </SiteLayout>
  );
}